<script setup>
import DesktopIcon from '@iconify-vue/ph/desktop';
import DeviceMobileIcon from '@iconify-vue/ph/device-mobile';
import PlusIcon from '@iconify-vue/ph/plus';
import TrashIcon from '@iconify-vue/ph/trash';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { toastDeleted } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    data,
    count,
    pagination,
    pageCount,
    sorting,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'url',
            header: () => 'URL',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.url,
                    href: props.row.original.url,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'devPageSpeedMetricsLast.desktopScore',
            header: () => 'Desktop score',
            cell: (props) =>
                props.row.original.lastDesktopScore
                    ? h(PillCell, {
                          pillValue: props.row.original.lastDesktopScore,
                          type:
                              props.row.original.lastDesktopScore >= 90
                                  ? 'success'
                                  : props.row.original.lastDesktopScore >= 50
                                    ? 'warning'
                                    : 'danger',
                          leftIcon: DesktopIcon,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'devPageSpeedMetricsLast.desktopWebCoreVitalsScore',
            header: () => 'Desktop Web Core Vitals',
            cell: (props) =>
                props.row.original.lastDesktopWebCoreVitalsScore
                    ? h(PillCell, {
                          pillValue:
                              props.row.original
                                  .lastDesktopWebCoreVitalsScore === 'FAST'
                                  ? 'Rapide'
                                  : props.row.original
                                          .lastDesktopWebCoreVitalsScore ===
                                      'AVERAGE'
                                    ? 'Moyen'
                                    : 'Lent',
                          type:
                              props.row.original
                                  .lastDesktopWebCoreVitalsScore === 'FAST'
                                  ? 'success'
                                  : props.row.original
                                          .lastDesktopWebCoreVitalsScore ===
                                      'AVERAGE'
                                    ? 'warning'
                                    : 'danger',
                          leftIcon: DesktopIcon,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'devPageSpeedMetricsLast.mobileScore',
            header: () => 'Score mobile',
            cell: (props) =>
                props.row.original.lastMobileScore
                    ? h(PillCell, {
                          pillValue: props.row.original.lastMobileScore,
                          type:
                              props.row.original.lastMobileScore >= 90
                                  ? 'success'
                                  : props.row.original.lastMobileScore >= 50
                                    ? 'warning'
                                    : 'danger',
                          leftIcon: DeviceMobileIcon,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'devPageSpeedMetricsLast.mobileWebCoreVitalsScore',
            header: () => 'Mobile Web Core Vitals',
            cell: (props) =>
                props.row.original.lastMobileWebCoreVitalsScore
                    ? h(PillCell, {
                          pillValue:
                              props.row.original
                                  .lastMobileWebCoreVitalsScore === 'FAST'
                                  ? 'Rapide'
                                  : props.row.original
                                          .lastMobileWebCoreVitalsScore ===
                                      'AVERAGE'
                                    ? 'Moyen'
                                    : 'Lent',
                          type:
                              props.row.original
                                  .lastMobileWebCoreVitalsScore === 'FAST'
                                  ? 'success'
                                  : props.row.original
                                          .lastMobileWebCoreVitalsScore ===
                                      'AVERAGE'
                                    ? 'warning'
                                    : 'danger',
                          leftIcon: DeviceMobileIcon,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'devPageSpeedMetricsLast.createdAt',
            header: () => 'Vérifié le',
            cell: (props) =>
                h(TextCell, {
                    text: formatDate(
                        props.row.original.lastVisitAt,
                        false,
                        true,
                    ),
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                DeleteAction,
                                {
                                    url: route(
                                        'front.projectManagement.dev.deletePageSpeedUrl',
                                        {
                                            tenant: tenant.id,
                                            id: props.row.original.id,
                                        },
                                    ),
                                    callback: () => {
                                        toastDeleted();
                                        updateData();
                                    },
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            type: 'secondary',
                                            icon: TrashIcon,
                                            size: 'small',
                                            square: true,
                                            title: 'Supprimer',
                                        }),
                                },
                            ),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.dev.postPageSpeed', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.dev.createPageSpeedUrl', {
                        tenant,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
